import { useFileDialog } from "@vueuse/core";
import {
  EFileTransactionStatus,
  useFileTransaction,
  type UseFileTransaction,
  type UseFileTransactionStartParams,
} from "./transaction";
import { notNullish, type IAttachment, type ISavedDocument } from "findef";
import { EventSystem } from "@/utils/event/eventSystem";

export type UseFileUploadProps = {
  startTransactionsOnDialogClose?: boolean;
  startParams?: UseFileTransactionStartParams;
};

export enum EUseFileUploadEventType {
  FILE_FINISHED = "FILE_FINISHED",
  FILE_GROUP_FINISHED = "FILE_GROUP_FINISHED",
  FILE_FAILED = "FILE_FAILED",
  FILE_CANCELED = "FILE_CANCELED",
  FILE_DELETED = "FILE_DELETED",
}

export type UseFileUploadEventMap = {
  [EUseFileUploadEventType.FILE_FINISHED]: {
    eventType: EUseFileUploadEventType.FILE_FINISHED;
    name: string;
    attachment: ISavedDocument<IAttachment>;
  };
  [EUseFileUploadEventType.FILE_GROUP_FINISHED]: {
    eventType: EUseFileUploadEventType.FILE_GROUP_FINISHED;
    files: Array<{
      name: string;
      attachment: ISavedDocument<IAttachment>;
    }>;
  };
  [EUseFileUploadEventType.FILE_FAILED]: {
    eventType: EUseFileUploadEventType.FILE_FAILED;
    name: string;
  };
  [EUseFileUploadEventType.FILE_CANCELED]: {
    eventType: EUseFileUploadEventType.FILE_CANCELED;
    name: string;
  };
  [EUseFileUploadEventType.FILE_DELETED]: {
    eventType: EUseFileUploadEventType.FILE_DELETED;
    name: string;
  };
};

export type UseFileUpload = {
  transactions: Ref<Map<string, UseFileTransaction>>;
  promises: Ref<Set<Promise<ISavedDocument<IAttachment> | null>>>;
  startAllTransactions: () => void;
  cancelAllTransactions: () => void;
  clearAllTransactions: () => void;
  cancelTransaction: (name: string) => void;
  deleteTransaction: (name: string) => void;
  openDialog: () => Promise<void>;
  events: EventSystem<UseFileUploadEventMap>;
  uploadFile: (file: File) => Promise<IAttachment | null>;
  uploadFiles: (files: Array<File>) => Promise<Array<IAttachment>>;
  __v_skip: true;
};

export const useFileUpload = (props: UseFileUploadProps): UseFileUpload => {
  const { startTransactionsOnDialogClose = true } = props;

  const events = new EventSystem<UseFileUploadEventMap>();

  const transactions = ref<Map<string, UseFileTransaction>>(new Map());
  const promises = ref<Set<Promise<ISavedDocument<IAttachment> | null>>>(
    new Set(),
  );
  const dialog = useFileDialog();

  const uploadFile = (file: File): Promise<IAttachment | null> => {
    if (transactions.value.has(file.name)) return Promise.resolve(null);
    const transaction = useFileTransaction({
      file: file,
      startParams: props.startParams,
    });

    return new Promise<IAttachment | null>((resolve) => {
      const unsubOKEvent = transaction.events.subscribe(
        EFileTransactionStatus.OK,
        (ev) => {
          events.emit({
            eventType: EUseFileUploadEventType.FILE_FINISHED,
            attachment: ev.attachment,
            name: ev.name,
          });
          queueMicrotask(() => unsubOKEvent());
          resolve(ev.attachment);
        },
      );
      const unsubFailEvent = transaction.events.subscribe(
        EFileTransactionStatus.FAILED,
        (ev) => {
          events.emit({
            eventType: EUseFileUploadEventType.FILE_FAILED,
            name: ev.name,
          });
          queueMicrotask(() => unsubFailEvent());
          resolve(null);
        },
      );
      transactions.value.set(file.name, transaction as unknown as any);
      transaction.start().catch(e => {
        console.error(e);
        resolve(null);
      })
    });
  };

  const uploadFiles = async (
    files: Array<File>,
  ): Promise<Array<IAttachment>> => {
    const attachments = (
      await Promise.allSettled(
        files.map(async (file) => await uploadFile(file)),
      )
    )
      .filter((it) => it.status === "fulfilled")
      .map((it) => it.value)
      .filter(notNullish);

    events.emit({
      eventType: EUseFileUploadEventType.FILE_GROUP_FINISHED,
      files: attachments.map((it) => ({
        name: it.name || '?',
        attachment: it as unknown as any as ISavedDocument<IAttachment>
      })),
    });

    return attachments;
  };

  const openDialog = () => {
    return new Promise<void>((resolve) => {
      dialog.open();

      const finishedArray: Array<{
        name: string;
        attachment: ISavedDocument<IAttachment>;
      }> = [];

      const addFile = (file: File, expectedToFinish: number) => {
        if (transactions.value.has(file.name)) return;
        const transaction = useFileTransaction({
          file: file,
          startParams: props.startParams,
        });

        let numFailed: number = 0;
        const unsubOKEvent = transaction.events.subscribe(
          EFileTransactionStatus.OK,
          (ev) => {
            finishedArray.push({
              name: ev.name,
              attachment: ev.attachment,
            });

            if (finishedArray.length + numFailed >= expectedToFinish) {
              events.emit({
                eventType: EUseFileUploadEventType.FILE_GROUP_FINISHED,
                files: finishedArray,
              });
              queueMicrotask(() => unsubOKEvent());
            }
          },
        );
        const unsubFailEvent = transaction.events.subscribe(
          EFileTransactionStatus.FAILED,
          (ev) => {
            numFailed += 1;
            events.emit({
              eventType: EUseFileUploadEventType.FILE_FAILED,
              name: ev.name,
            });
            queueMicrotask(() => unsubFailEvent());
          },
        );
        transactions.value.set(file.name, transaction as unknown as any);
      };

      const hookFn = dialog.onChange((files: FileList | null) => {
        if (!files) {
          resolve();
          return;
        }
        const fileArray = Array.from(files);
        const newFiles = fileArray.filter(
          (it) => !transactions.value.has(it.name),
        );
        newFiles.forEach((file) => addFile(file, newFiles.length));

        if (startTransactionsOnDialogClose) {
          queueMicrotask(() => {
            startAllTransactions();
          });
        }

        queueMicrotask(() => {
          hookFn.off();
          resolve();
        });
      });
    });
  };

  const startAllTransactions = () => {
    transactions.value.forEach((trans) => {
      const promise = trans.start();
      promises.value.add(promise);
      promise.finally(() => {
        promises.value.delete(promise);
      });
    });
  };

  const cancelAllTransactions = () => {
    transactions.value.forEach((trans) => {
      trans.cancel();
      events.emit({
        eventType: EUseFileUploadEventType.FILE_CANCELED,
        name: trans.name,
      });
    });
  };

  const clearAllTransactions = () => {
    cancelAllTransactions();
    transactions.value.clear();
  };

  const cancelTransaction = (name: string) => {
    const trans = transactions.value.get(name);
    if (!trans) return;
    trans.cancel();
    events.emit({
      eventType: EUseFileUploadEventType.FILE_CANCELED,
      name: name,
    });
  };

  const deleteTransaction = (name: string) => {
    cancelTransaction(name);
    transactions.value.delete(name);
    events.emit({
      eventType: EUseFileUploadEventType.FILE_DELETED,
      name: name,
    });
  };

  return {
    openDialog,
    startAllTransactions,
    cancelAllTransactions,
    clearAllTransactions,
    cancelTransaction,
    deleteTransaction,
    promises,
    events,
    transactions: transactions as unknown as any as Ref<
      Map<string, UseFileTransaction>
    >,
    uploadFile,
    uploadFiles,
    __v_skip: true,
  };
};
